import React from 'react';
import classNames from 'classnames';
import Badge, { BadgeType } from 'src/components/ui/Badge/Badge';
import Input from 'src/components/ui/Input/Input';
import Icon from 'src/components/ui/Icon/Icon';
import { formatDateAndTime, formatAmount } from 'src/utils/general';
import { useTranslation } from 'react-i18next';
import CarrierSelect from 'src/components/ui/CarrierSelect/CarrierSelect';
import OrderTagEditor from 'src/components/ui/OrderTagEditor/OrderTagEditor';
import Toggle from 'src/components/ui/Toggle/Toggle';
import Select, { Option } from 'src/components/ui/Select/Select';
import { SaleOrderReadExt, SaleOrderUpdateExt } from 'src/services/saleOrder';
import { useAvailableIncotermsQuery } from 'src/services/incoterm';
import { useCodSettingsQuery } from 'src/services/codSettings';
import { mergePatchValue } from '../mergePatchValue';
import styles from './PrimaryInfo.module.scss';
import { useDescriptionsQuery } from '../../../../services/utility';
import { useProfile } from '../../../../utils/ProfileService';
import WarehouseSelect from '../../../ui/WarehouseSelect/WarehouseSelect';

export interface PrimaryInfoProps {
  saleOrder: SaleOrderReadExt;
  edit?: boolean;
  saleOrderItem: SaleOrderUpdateExt;
  updateSaleOrderItem: (key: string, value: any) => void;
}

const PrimaryInfo: React.FC<PrimaryInfoProps> = ({ saleOrder, edit, saleOrderItem, updateSaleOrderItem }) => {
  const { t } = useTranslation();
  const { profile } = useProfile();
  const { data: descriptions } = useDescriptionsQuery('sale.order');
  const { data: incoterms } = useAvailableIncotermsQuery();
  const { data: codSettings } = useCodSettingsQuery();

  const incotermOption = (id: number | null | undefined): Option | undefined => {
    const incoterm = incoterms?.find(i => i.id === id);
    return incoterm ? { label: `${incoterm.code} — ${incoterm.name}`, value: String(incoterm.id) } : undefined;
  };

  const codCurrencyOption = (id: number | null | undefined): Option | undefined => {
    const currency = codSettings?.currencies.find(c => c.id === id);
    return currency ? { label: `${currency.code} — ${currency.name}`, value: String(currency.id) } : undefined;
  };

  const codCurrencyCode = (id: number | null | undefined): string | undefined =>
    codSettings?.currencies.find(c => c.id === id)?.code;

  const isSelfCollection =
    saleOrderItem.is_self_collection !== undefined
      ? saleOrderItem.is_self_collection
      : saleOrder.is_self_collection ?? false;

  // Merge patch-over-original via the shared `mergePatchValue` helper (see
  // that module for why `!== undefined`, not `??`): the toggle-off handler
  // below explicitly nulls cod_amount / cod_currency_id ("leeren" semantics
  // per spec), and the incoterm Select's onChange explicitly nulls
  // incoterm_id on clear — falling back to the original saleOrder value on
  // an explicit null would silently resurrect stale data and break both the
  // displayed value and (for COD) the save validation.
  const isCod = mergePatchValue(saleOrderItem.is_cod, saleOrder.is_cod) ?? false;
  const codAmount = mergePatchValue(saleOrderItem.cod_amount, saleOrder.cod_amount) ?? null;
  const codCurrencyId = mergePatchValue(saleOrderItem.cod_currency_id, saleOrder.cod_currency_id) ?? null;
  const incotermId = mergePatchValue(saleOrderItem.incoterm_id, saleOrder.incoterm_id);

  const renderBadgeOrText = (label: string | undefined, placeholder: string) =>
    label ? (
      <Badge label={label} expanded type={BadgeType.Neutral} />
    ) : (
      <span className="typo-text-14 text-full">{placeholder}</span>
    );
  return (
    <div className={classNames(styles.root)}>
      <div className="flex flex-col gap-y-16">
        <div className={styles.info}>
          <span className="typo-text-14--bold text-faded">{t('general.internal_id')}:</span>
          <Badge label={String(saleOrder?.id)} expanded type={BadgeType.Neutral} />
        </div>

        <div className={classNames(styles.info, 'items-center')}>
          <span className="typo-text-14--bold text-faded">{t('general.reference')}:</span>
          {!edit ? (
            renderBadgeOrText(saleOrder.name, '-')
          ) : (
            <Input
              placeholder={saleOrder.name || t('general.name')}
              value={saleOrderItem.name ?? saleOrder.name}
              onChange={e => updateSaleOrderItem('name', e.target.value)}
            />
          )}
        </div>

        <div className={styles.info}>
          <span className="typo-text-14--bold text-faded flex items-center gap-1">
            {t('general.date_order')}
            {descriptions?.date_order && (
              <div className="relative group">
                <Icon name="info" width={16} height={16} className="text-faded cursor-pointer" />
                <div
                  className="
                    absolute left-full top-1/2 -translate-y-1/2 ml-2
                    p-6 bg-color-grey-400 text-white typo-text-12 rounded
                    opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-10
                    max-w-xs w-max whitespace-normal font-sans
                  ">
                  {descriptions?.date_order}
                </div>
              </div>
            )}
            :
          </span>
          <span className="typo-text-14 text-full">
            {saleOrder.date_order
              ? formatDateAndTime(String(saleOrder.date_order), profile.lang ? profile.lang.replace('_', '-') : 'en-GB')
              : '-'}
          </span>
        </div>
        <div className={styles.info}>
          <span className="typo-text-14--bold text-faded flex items-center gap-1">
            {t('general.import_date')}
            {descriptions?.import_date && (
              <div className="relative group">
                <Icon name="info" width={16} height={16} className="text-faded cursor-pointer" />
                <div
                  className="
                    absolute left-full top-1/2 -translate-y-1/2 ml-2
                    p-6 bg-color-grey-400 text-white typo-text-12 rounded
                    opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-10
                    max-w-xs w-max whitespace-normal font-sans
                  ">
                  {descriptions?.import_date}
                </div>
              </div>
            )}
            :
          </span>
          <span className="typo-text-14 text-full">
            {saleOrder.import_date
              ? formatDateAndTime(
                  String(saleOrder.import_date),
                  profile.lang ? profile.lang.replace('_', '-') : 'en-GB',
                )
              : '-'}
          </span>
        </div>
        <div className={styles.info}>
          <span className="typo-text-14--bold text-faded flex items-center gap-1">
            {t('general.commitment_date')}
            {descriptions?.commitment_date && (
              <div className="relative group">
                <Icon name="info" width={16} height={16} className="text-faded cursor-pointer" />
                <div
                  className="
                    absolute left-full top-1/2 -translate-y-1/2 ml-2
                    p-6 bg-color-grey-400 text-white typo-text-12 rounded
                    opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-10
                    max-w-xs w-max whitespace-normal font-sans
                  ">
                  {descriptions?.commitment_date}
                </div>
              </div>
            )}
            :
          </span>
          <span className="typo-text-14 text-full">
            {saleOrder.commitment_date
              ? formatDateAndTime(
                  String(saleOrder.commitment_date),
                  profile.lang ? profile.lang.replace('_', '-') : 'en-GB',
                )
              : '-'}
          </span>
        </div>

        <div className={styles.info}>
          <span className="typo-text-14--bold text-faded">{t('general.channel')}:</span>
          {saleOrder?.connector_name ? (
            <Badge label={String(saleOrder?.connector_name)} expanded type={BadgeType.Neutral} />
          ) : (
            <span className="typo-text-14 text-full">-</span>
          )}
        </div>
        <div className={classNames(styles.info, 'items-start')}>
          <span className="typo-text-14--bold text-faded">{t('navbar.warehouse')}*</span>
          <div className="flex flex-col flex-1 gap-16">
            <WarehouseSelect
              disabled={!edit}
              defaultWarehouse={saleOrder.warehouse?.id}
              defaultWarehouseName={saleOrder.warehouse?.name}
              onChange={warehouseId => {
                updateSaleOrderItem('warehouse_id', warehouseId);
              }}
            />
          </div>
        </div>
        {!isSelfCollection && (
          <div className={classNames(styles.info, 'items-start')}>
            <span className="typo-text-14--bold text-faded">{t('general.carrier')}</span>
            <div className="flex flex-col flex-1 gap-16">
              <CarrierSelect
                disabled={!edit}
                defaultCarrier={mergePatchValue(saleOrderItem.carrier_id, saleOrder.carrier?.id) ?? undefined}
                onChange={carrier => {
                  // null, not undefined: a cleared carrier must survive JSON to reach the backend
                  updateSaleOrderItem('carrier_id', carrier ?? null);
                }}
              />
            </div>
          </div>
        )}
        {incoterms && incoterms.length > 0 && (
          <div className={classNames(styles.info, 'items-start')}>
            <span className="typo-text-14--bold text-faded">{t('outbound.incoterm')}</span>
            <div className="flex flex-col flex-1 gap-16">
              <Select
                id="edit-order-incoterm"
                options={incoterms.map(i => ({ label: `${i.code} — ${i.name}`, value: String(i.id) }))}
                defaultValue={incotermOption(incotermId)}
                onChange={option => updateSaleOrderItem('incoterm_id', option ? Number(option.value) : null)}
                disabled={!edit}
                isInfoDisplay={!edit}
                isClearable={!!edit}
                fullWidth
              />
            </div>
          </div>
        )}
        {codSettings?.enabled && (
          <div className={classNames(styles.info, 'items-start')}>
            <span className="typo-text-14--bold text-faded">{t('outbound.cod_toggle')}</span>
            <div className="flex flex-col flex-1 gap-16">
              {!edit ? (
                <span className="typo-text-14 text-full">
                  {isCod
                    ? `${formatAmount(codAmount ?? 0, profile.lang ?? null)} ${codCurrencyCode(codCurrencyId) ?? ''}`.trim()
                    : '-'}
                </span>
              ) : (
                <>
                  <Toggle
                    checked={isCod}
                    onToggle={checked => {
                      updateSaleOrderItem('is_cod', checked);
                      if (!checked) {
                        updateSaleOrderItem('cod_amount', null);
                        updateSaleOrderItem('cod_currency_id', null);
                      }
                    }}
                  />
                  {isCod && (
                    <div className="grid grid-cols-2 gap-16">
                      <Input
                        id="edit-order-cod-amount"
                        type="number"
                        min={0}
                        label={t('outbound.cod_amount')}
                        placeholder={t('outbound.cod_amount')}
                        value={codAmount ?? ''}
                        onChange={e =>
                          updateSaleOrderItem('cod_amount', e.target.value === '' ? null : Number(e.target.value))
                        }
                      />
                      <Select
                        id="edit-order-cod-currency"
                        label={t('outbound.cod_currency')}
                        options={(codSettings.currencies ?? []).map(c => ({
                          label: `${c.code} — ${c.name}`,
                          value: String(c.id),
                        }))}
                        defaultValue={codCurrencyOption(codCurrencyId)}
                        onChange={option =>
                          updateSaleOrderItem('cod_currency_id', option ? Number(option.value) : null)
                        }
                        isClearable
                        fullWidth
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
        <div className={classNames(styles.info, 'items-center')}>
          <span className="typo-text-14--bold text-faded">{t('general.is_self_collection')}</span>
          <Toggle
            disabled={!edit || saleOrder.fulfillment_state?.key !== 'draft'}
            checked={isSelfCollection}
            onToggle={checked => updateSaleOrderItem('is_self_collection', checked)}
          />
        </div>
        <div className={classNames(styles.info, 'items-start')}>
          <span className="typo-text-14--bold text-faded invisible">{t('general.tags')}:</span>
          {saleOrder.id ? (
            <OrderTagEditor saleOrderId={saleOrder.id} tags={saleOrder.tags ?? []} />
          ) : (
            <span className="typo-text-14 text-full">-</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrimaryInfo;
